import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertTriangle, Edit2 } from 'lucide-react';
import { acknowledgementService } from '../../services/apiService';
import Select from '../../components/ui/Select';
import {
  getQuestionnaireLabel,
  supplierTermsOfPayment,
  supplierPaymentMethods,
  supplierDeliveryTerms,
  supplierModeOfDelivery,
} from '../../utils/questionnaireOptions';
import { useToast } from '../../components/ui/Toast';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';

const PublicVendorAcknowledgement = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirmState, confirm, setConfirmState } = useConfirm();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [acknowledgement, setAcknowledgement] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [wasEditing, setWasEditing] = useState(false);

  useEffect(() => {
    if (!token || token.length < 10) {
      setError('Invalid acknowledgement link');
      setLoading(false);
      return;
    }

    validateToken();
  }, [token]);

  const validateToken = async () => {
    try {
      setLoading(true);
      const response = await acknowledgementService.validateToken(token);
      setAcknowledgement(response.data);
      setEditedData(response.data.questionnaire_data || {});
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to validate acknowledgement link');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    const confirmed = await confirm({
      title: 'Approve Questionnaire',
      message: 'Are you sure you want to approve this questionnaire?',
      variant: 'info'
    });

    if (!confirmed) return;

    try {
      setSubmitting(true);
      await acknowledgementService.approve(token);
      setSuccess(true);
      setWasEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve questionnaire');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConflict = async () => {
    const confirmed = await confirm({
      title: 'Submit Changes',
      message: 'Are you sure you want to submit these changes for review?',
      variant: 'warning'
    });

    if (!confirmed) return;

    try {
      setSubmitting(true);
      await acknowledgementService.conflict(token, { vendor_response: editedData });
      setSuccess(true);
      setWasEditing(true);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit changes');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setEditedData((prev) => ({ ...prev, [field]: value }));
  };

  // Helper function to get full label from stored code
  const getFieldLabel = (fieldKey, value) => {
    return getQuestionnaireLabel(fieldKey, value);
  };

  // Map field keys to their corresponding option arrays
  const fieldOptionsMap = {
    supplier_term_of_payment: supplierTermsOfPayment,
    supplier_payment_method: supplierPaymentMethods,
    supplier_delivery_terms: supplierDeliveryTerms,
    supplier_mode_of_delivery: supplierModeOfDelivery,
  };

  const fields = [
    { key: 'supplier_term_of_payment', label: 'Term of Payment' },
    { key: 'supplier_payment_method', label: 'Payment Method' },
    { key: 'supplier_delivery_terms', label: 'Delivery Terms' },
    { key: 'supplier_mode_of_delivery', label: 'Mode of Delivery' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Validating acknowledgement link...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Link</h2>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {wasEditing ? 'Changes Submitted' : 'Questionnaire Approved'}
          </h2>
          <p className="text-gray-600 mb-6">
            {wasEditing
              ? 'Your changes have been submitted for admin review. You will be notified once reviewed.'
              : 'Thank you for approving the questionnaire. Your application is now proceeding to Level 2.'}
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="w-8 h-8 text-amber-500 mr-3" />
            <h1 className="text-3xl font-bold text-gray-900">Acknowledge vendor questionnaire</h1>
          </div>
          <p className="text-gray-600">
            Company: <strong>{acknowledgement?.vendor?.company_name}</strong>
          </p>
          <p className="text-sm text-gray-500 mt-2">
            Please review the questionnaire details filled by our team. You can either approve them or propose changes.
          </p>
        </div>

        {/* Questionnaire Details */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Questionnaire Details</h2>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit & Propose Changes
              </button>
            )}
          </div>

          <div className="space-y-4">
            {fields.map((field) => (
              <div key={field.key} className="border-b border-gray-200 pb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {field.label}
                </label>
                {isEditing ? (
                  <Select
                    options={fieldOptionsMap[field.key] || []}
                    value={editedData[field.key] || ''}
                    onChange={(value) => handleFieldChange(field.key, value)}
                    placeholder={`Select ${field.label}`}
                    searchable
                    clearable
                  />
                ) : (
                  <p className="text-gray-900 bg-gray-50 px-4 py-2 rounded-lg">
                    {getFieldLabel(field.key, acknowledgement?.questionnaire_data?.[field.key])}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex flex-col sm:flex-row gap-4">
            {isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(false)}
                  disabled={submitting}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConflict}
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Changes for Review'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  disabled={submitting}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Propose Changes
                </button>
                <button
                  onClick={handleApprove}
                  disabled={submitting}
                  className="flex-1 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {submitting ? 'Approving...' : 'Approve Questionnaire'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Expiry Notice */}
        <div className="mt-6 text-center text-sm text-gray-500">
          This link expires on{' '}
          {new Date(acknowledgement?.expires_at).toLocaleDateString('en-IN', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </div>
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmState.isOpen}
        onClose={confirmState.onCancel}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        confirmText={confirmState.confirmText}
        cancelText={confirmState.cancelText}
      />
    </div>
  );
};

export default PublicVendorAcknowledgement;
